import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import {
  PortalApiResponse,
  PortalConsentPolicy,
  PortalConsentStatus,
  PortalRelationship,
  PortalSessionStatus,
} from '../models/portal-comun.model';
import { PortalApiService, PortalSessionEvents } from '../services/portal-api.service';

/**
 * Vínculo cuenta ↔ contrato ↔ espacio.
 *
 * <p>Los tres permisos viajan EXPLÍCITOS desde el backend: ser cotitular no implica poder pagar y
 * ser beneficiario no implica poder descargar el acta. Un vínculo con `revokedAt` sigue llegando
 * —para que el espacio se siga viendo— pero en SOLO LECTURA (`readOnly`).</p>
 */
export interface PortalLink {
  linkId: number;
  contractId: number;
  spaceId: number;
  spaceCode: string | null;
  relationship: PortalRelationship;
  holderId: number | null;
  canPay: boolean;
  canRequest: boolean;
  canDownloadSensitive: boolean;
  revokedAt: string | null;
  effectiveUntil: string | null;
  /** Derivado de `revokedAt`: el portal nunca decide esto por su cuenta. */
  readOnly: boolean;
}

/** Cuenta del portal. Solo datos enmascarados: el documento y el correo en claro no salen del backend. */
export interface PortalAccount {
  accountId: number;
  displayName: string;
  docMasked: string;
  docType: string | null;
  accountType: string;
  emailMasked: string | null;
  largeText: boolean;
  lastLoginAt: string | null;
}

/** Respuesta cruda de `GET /me`. */
interface PortalMeResponse {
  account: {
    accountId: number;
    companyId: number;
    displayName: string;
    docMasked: string;
    docType: string | null;
    accountType: string;
    emailMasked: string | null;
    largeText: boolean | null;
  } | null;
  links: RawLink[] | null;
  preferences: { largeText: boolean | null; preferredLang: string | null } | null;
  consent: PortalConsentStatus | null;
  currentPolicy: PortalConsentPolicy | null;
  lastLoginAt: string | null;
}

interface RawLink {
  linkId: number;
  contractId: number;
  spaceId: number;
  spaceCode: string | null;
  relationship: PortalRelationship;
  holderId: number | null;
  canPay: boolean | null;
  canRequest: boolean | null;
  canDownloadSensitive: boolean | null;
  revokedAt: string | null;
  effectiveUntil: string | null;
}

const LARGE_TEXT_KEY = 'portal.largeText';

/**
 * ESTADO DE LA SESIÓN del Portal de Familias.
 *
 * <p>Es el único sitio donde vive «quién es la persona que está mirando la pantalla». No hay
 * `UsuarioService` ni `PermissionService`: en el portal los permisos no son módulos y acciones,
 * son TRES banderas por espacio (`canPay`, `canRequest`, `canDownloadSensitive`) que el backend
 * calcula a partir del contrato. La pantalla nunca las deduce.</p>
 *
 * <p>La cuenta atrás de sesión se decrementa en local y se re-sincroniza cada minuto contra
 * `GET /auth/session`: pedirla cada segundo sería un sondeo inútil, y no pedirla nunca haría que
 * el reloj mintiera en cuanto otra pestaña renovara la sesión.</p>
 */
@Injectable({ providedIn: 'root' })
export class PortalAuthService {
  private readonly api = inject(PortalApiService);
  private readonly router = inject(Router);

  /** RUC de la empresa tal como viaja en la URL `/portal/{empresa}/…`. */
  readonly empresa: WritableSignal<string> = signal('');

  private readonly _account = signal<PortalAccount | null>(null);
  private readonly _links = signal<PortalLink[]>([]);
  private readonly _consent = signal<PortalConsentStatus | null>(null);
  private readonly _policy = signal<PortalConsentPolicy | null>(null);
  private readonly _largeText = signal<boolean>(leerPreferenciaLocal());
  private readonly _remainingSeconds = signal<number | null>(null);

  readonly account: Signal<PortalAccount | null> = this._account.asReadonly();
  readonly links: Signal<PortalLink[]> = this._links.asReadonly();
  readonly consent: Signal<PortalConsentStatus | null> = this._consent.asReadonly();
  readonly currentPolicy: Signal<PortalConsentPolicy | null> = this._policy.asReadonly();
  readonly largeText: Signal<boolean> = this._largeText.asReadonly();
  readonly remainingSeconds: Signal<number | null> = this._remainingSeconds.asReadonly();

  readonly consentRequired = computed(() => this._consent()?.required === true);
  readonly autenticado = computed(() => this._account() !== null);
  /** Vínculos VIVOS: los revocados se muestran, pero no autorizan nada. */
  readonly vinculosVivos = computed(() => this._links().filter(l => !l.readOnly));
  readonly canPay = computed(() => this.vinculosVivos().some(l => l.canPay));
  readonly canRequest = computed(() => this.vinculosVivos().some(l => l.canRequest));

  /** Ticker de la cuenta atrás; vive mientras haya sesión. */
  private tick: ReturnType<typeof setInterval> | null = null;

  constructor() {
    inject(PortalSessionEvents).register(this);
    aplicarClaseTextoGrande(this._largeText());
  }

  // ── Carga ────────────────────────────────────────────────────────────────────────────────

  /** `GET /me`: cuenta, vínculos, preferencias y consentimiento en una sola llamada. */
  async loadMe(): Promise<boolean> {
    try {
      const me = await firstValueFrom(this.api.get<PortalMeResponse>('/me'));
      this.aplicarMe(me);
      this.arrancarReloj();
      return true;
    } catch {
      this.limpiar();
      return false;
    }
  }

  /** Vuelca en los signals la respuesta de `/me` (o la de `verify-otp`, que tiene la misma forma). */
  aplicarMe(me: PortalMeResponse): void {
    const cuenta = me.account;
    const largeText = me.preferences?.largeText ?? cuenta?.largeText ?? this._largeText();
    this._account.set(cuenta
      ? {
        accountId: cuenta.accountId,
        displayName: cuenta.displayName,
        docMasked: cuenta.docMasked,
        docType: cuenta.docType ?? null,
        accountType: cuenta.accountType,
        emailMasked: cuenta.emailMasked ?? null,
        largeText: largeText === true,
        lastLoginAt: me.lastLoginAt ?? null,
      }
      : null);
    this._links.set((me.links ?? []).map(mapearVinculo));
    this._consent.set(me.consent ?? null);
    this._policy.set(me.currentPolicy ?? null);
    this.aplicarTextoGrande(largeText === true, false);
  }

  /** Estado de la sesión (`GET /auth/session`) → resincroniza la cuenta atrás. */
  async syncSession(): Promise<PortalSessionStatus | null> {
    try {
      const estado = await firstValueFrom(this.api.get<PortalSessionStatus>('/auth/session'));
      if (!estado.active) {
        this._remainingSeconds.set(0);
        return estado;
      }
      // El tope absoluto (12 h) manda si es el más cercano: es el que realmente va a cortar.
      const restante = Math.min(
        estado.remainingSeconds,
        estado.absoluteRemainingSeconds > 0 ? estado.absoluteRemainingSeconds : estado.remainingSeconds,
      );
      this._remainingSeconds.set(Math.max(0, Math.floor(restante)));
      return estado;
    } catch {
      return null;
    }
  }

  // ── Sesión ───────────────────────────────────────────────────────────────────────────────

  /** Renueva el access token («Seguir conectado» del aviso de expiración). */
  async refresh(): Promise<boolean> {
    try {
      await firstValueFrom(this.api.post<PortalApiResponse>('/auth/refresh'));
      await this.syncSession();
      return true;
    } catch {
      return false;
    }
  }

  /** Cierra ESTA sesión y vuelve a la pantalla de ingreso. */
  async logout(): Promise<void> {
    try {
      await firstValueFrom(this.api.post<PortalApiResponse>('/auth/logout'));
    } catch {
      // Da igual por qué falló: localmente la sesión se acaba igual.
    }
    this.limpiar();
    void this.router.navigate([this.portalPath('acceso')]);
  }

  /** Cierra TODAS las sesiones de la cuenta (`POST /me/logout-all`). */
  async logoutAll(): Promise<string | null> {
    try {
      const r = await firstValueFrom(this.api.post<PortalApiResponse>('/me/logout-all'));
      this.limpiar();
      void this.router.navigate([this.portalPath('acceso')]);
      return r?.message ?? null;
    } catch {
      return null;
    }
  }

  /** La sesión murió (401 tras renovar, o la cuenta atrás llegó a cero). */
  handleSessionLost(): void {
    if (!this._account() && !this.tick) {
      return;
    }
    this.limpiar();
    void this.router.navigate([this.portalPath('acceso')], { queryParams: { expired: 1 } });
  }

  /** Hay política nueva sin aceptar: el portal queda bloqueado hasta el paso 3 del ingreso. */
  requireConsent(): void {
    this._consent.set({ ...(this._consent() ?? { policyId: null, policyVersion: null, policyTitle: null, acceptedAt: null }), required: true });
    void this.router.navigate([this.portalPath('acceso')], { queryParams: { paso: 'consentimiento' } });
  }

  // ── Preferencias ─────────────────────────────────────────────────────────────────────────

  /** Texto grande: se guarda en el navegador (efecto inmediato) y en la cuenta (viaja con la persona). */
  setLargeText(valor: boolean): void {
    this.aplicarTextoGrande(valor, true);
  }

  private aplicarTextoGrande(valor: boolean, persistirEnCuenta: boolean): void {
    this._largeText.set(valor);
    try {
      localStorage.setItem(LARGE_TEXT_KEY, valor ? '1' : '0');
    } catch {
      // Modo privado sin almacenamiento: la preferencia dura lo que la pestaña.
    }
    aplicarClaseTextoGrande(valor);
    const cuenta = this._account();
    if (cuenta) {
      this._account.set({ ...cuenta, largeText: valor });
    }
    if (persistirEnCuenta && cuenta) {
      this.api.post('/me/preferences', { largeText: valor }).subscribe({ error: () => undefined });
    }
  }

  // ── Utilidades ───────────────────────────────────────────────────────────────────────────

  /** `/portal/{empresa}/inicio` — todas las navegaciones del portal pasan por aquí. */
  portalPath(...segments: (string | number)[]): string {
    const empresa = this.empresa() || '_';
    return `/portal/${empresa}${segments.length ? '/' + segments.join('/') : ''}`;
  }

  /** Vínculo vivo de un espacio, si lo hay. */
  linkDeEspacio(spaceId: number): PortalLink | null {
    return this._links().find(l => l.spaceId === spaceId) ?? null;
  }

  private arrancarReloj(): void {
    void this.syncSession();
    if (this.tick) {
      return;
    }
    this.tick = setInterval(() => {
      const actual = this._remainingSeconds();
      if (actual === null) {
        return;
      }
      this._remainingSeconds.set(Math.max(0, actual - 1));
    }, 1000);
  }

  private limpiar(): void {
    this._account.set(null);
    this._links.set([]);
    this._consent.set(null);
    this._remainingSeconds.set(null);
    if (this.tick) {
      clearInterval(this.tick);
      this.tick = null;
    }
  }
}

function mapearVinculo(l: RawLink): PortalLink {
  const readOnly = l.revokedAt !== null && l.revokedAt !== undefined;
  return {
    linkId: l.linkId,
    contractId: l.contractId,
    spaceId: l.spaceId,
    spaceCode: l.spaceCode ?? null,
    relationship: l.relationship,
    holderId: l.holderId ?? null,
    // Un vínculo revocado no autoriza NADA aunque el permiso siga en true en la base.
    canPay: !readOnly && l.canPay === true,
    canRequest: !readOnly && l.canRequest === true,
    canDownloadSensitive: !readOnly && l.canDownloadSensitive === true,
    revokedAt: l.revokedAt ?? null,
    effectiveUntil: l.effectiveUntil ?? null,
    readOnly,
  };
}

function leerPreferenciaLocal(): boolean {
  try {
    return localStorage.getItem(LARGE_TEXT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Marca el `body` para que el tamaño base suba en TODO el portal, incluidos los modales que el
 * CDK cuelga fuera del árbol del layout.
 */
function aplicarClaseTextoGrande(valor: boolean): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.body.classList.toggle('portal-large-text', valor);
}
